import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { httpResource } from '@angular/common/http';
import { OnboardingFunnelService } from '../../Modules/Onboardings/Services/onboarding-funnel.service';
import { ResourceStateService } from '../../Modules/Shared/Services/resource-state.service';
import { ErrorHandlerService } from '../../Modules/Shared/Services/error-handler.service';
import { ExternalLinkService } from '../../Modules/Shared/Services/external-link.service';
import { MsgService } from '../../Modules/Shared/Services/msg.service';
import { OperatorFacade } from '../operator.facade';
import { environment } from '../../../environments/environment';
import { DEFAULT_PAGE_SIZE, FIRST_PAGE } from '../../Consts/backoffice.const';
import { ROLE_TENANT_HEALTH, TENANT_HEALTH_RAN_MESSAGE } from '../../Consts/onboardings.const';
import { MetricState } from '../../Modules/Shared/Types/metric-state.type';
import { OnboardingFunnelEntry } from '../../Models/backoffice/onboarding-funnel.model';

import { LaravelDefaultResponse } from '../../Interfaces/api/shared/response.interface';
import {
  OnboardingListResponse,
  TenantHealthCheckResponse,
} from '../../Interfaces/api/backoffice/onboarding.interface';

@Injectable()
export class OnboardingsFacade {
  private readonly _funnelService = inject(OnboardingFunnelService);
  private readonly _resourceState = inject(ResourceStateService);
  private readonly _errorHandler = inject(ErrorHandlerService);
  private readonly _externalLink = inject(ExternalLinkService);
  private readonly _msgService = inject(MsgService);
  private readonly _operatorFacade = inject(OperatorFacade);
  private readonly _destroyRef = inject(DestroyRef);

  private readonly _status = signal<string>('');
  private readonly _page = signal<number>(FIRST_PAGE);
  private readonly _pageSize = signal<number>(DEFAULT_PAGE_SIZE);
  private readonly _isMutating = signal<boolean>(false);
  private readonly _lastCheck = signal<TenantHealthCheckResponse | null>(null);

  private readonly _onboardings = httpResource<LaravelDefaultResponse<OnboardingListResponse>>(() =>
    this._funnelService.listRequest(this._status(), this._page(), this._pageSize()),
  );

  readonly status = computed<string>(() => this._status());

  readonly page = computed<number>(() => this._page());

  readonly pageSize = computed<number>(() => this._pageSize());

  readonly onboardings = computed<OnboardingFunnelEntry[]>(() =>
    this._funnelService.toEntries(this._resourceState.valueOf(this._onboardings)),
  );

  readonly summary = computed<Record<string, number>>(() =>
    this._funnelService.toSummary(this._resourceState.valueOf(this._onboardings)),
  );

  readonly total = computed<number>(() => this._funnelService.toTotal(this._resourceState.valueOf(this._onboardings)));

  readonly state = computed<MetricState>(() =>
    this._resourceState.metricState(this._onboardings.status()),
  );

  readonly isMutating = computed<boolean>(() => this._isMutating());

  readonly lastCheck = computed<TenantHealthCheckResponse | null>(() => this._lastCheck());

  readonly canCheckHealth = computed<boolean>(
    () => this._operatorFacade.atLeast(ROLE_TENANT_HEALTH) && !this._isMutating(),
  );

  filterStatus(status: string): void {
    this._status.set(status);
    this._page.set(FIRST_PAGE);
  }

  goToPage(page: number): void {
    this._page.set(Math.max(FIRST_PAGE, page));
  }

  changePageSize(pageSize: number): void {
    this._pageSize.set(pageSize);
    this._page.set(FIRST_PAGE);
  }

  reload(): void {
    this._onboardings.reload();
  }

  summaryFor(status: string): number {
    return this.summary()[status] ?? 0;
  }

  consentUrl(tenantId: string): string {
    const base = environment.coreOnboarding.host.replace(/\/+$/, '');

    return `${base}/setup/connect-intune?tenant=${encodeURIComponent(tenantId)}&source=backoffice`;
  }

  openConsent(tenantId: string): void {
    this._externalLink.openInNewTab(this.consentUrl(tenantId));
  }

  checkTenantHealth(organizationId: string): void {
    if (!this.canCheckHealth()) {
      return;
    }

    this._isMutating.set(true);

    this._funnelService
      .checkTenantHealth(organizationId)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (check) => this._afterCheck(check),
        error: (error) => this._failCheck(error),
      });
  }

  private _afterCheck(check: TenantHealthCheckResponse): void {
    this._isMutating.set(false);
    this._lastCheck.set(check);
    this._onboardings.reload();
    this._msgService.showSnackBar(TENANT_HEALTH_RAN_MESSAGE, { type: 'success' });
  }

  private _failCheck(error: unknown): void {
    this._isMutating.set(false);
    this._msgService.showSnackBar(this._errorHandler.messageFor(error), { type: 'error' });
  }
}
